// src/lib/logger.js
/**
 * Centralized logging utility
 *
 * In production, this can be extended to send logs to:
 * - Firebase Analytics
 * - Sentry
 * - CloudWatch
 * - Custom logging service
 */

const isDevelopment = import.meta.env.DEV;

/**
 * Log levels
 */
export const LogLevel = {
  DEBUG: "debug",
  INFO: "info",
  WARN: "warn",
  ERROR: "error",
};

/**
 * Logger class
 */
class Logger {
  constructor() {
    this.logs = [];
    this.maxLogs = 100; // Keep last 100 logs in memory (for debugging)
  }

  /**
   * Add log to memory buffer
   */
  _addToBuffer(level, message, data) {
    if (this.logs.length >= this.maxLogs) {
      this.logs.shift(); // Remove oldest log
    }
    this.logs.push({
      level,
      message,
      data,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Debug logs - only in development
   */
  debug(message, data = null) {
    if (isDevelopment) {
      console.log(`[DEBUG] ${message}`, data || "");
    }
    this._addToBuffer(LogLevel.DEBUG, message, data);
  }

  /**
   * Info logs - important information
   */
  info(message, data = null) {
    if (isDevelopment) {
      console.info(`[INFO] ${message}`, data || "");
    }
    this._addToBuffer(LogLevel.INFO, message, data);

    // TODO: In production, send to analytics service
    // if (window.gtag) {
    //   window.gtag('event', 'info_log', { message });
    // }
  }

  /**
   * Warning logs - potential issues
   */
  warn(message, data = null) {
    console.warn(`[WARN] ${message}`, data || "");
    this._addToBuffer(LogLevel.WARN, message, data);

    // TODO: In production, send to error tracking
    // if (window.Sentry) {
    //   window.Sentry.captureMessage(message, 'warning');
    // }
  }

  /**
   * Error logs - actual errors
   */
  error(message, error = null, data = null) {
    console.error(`[ERROR] ${message}`, error || "", data || "");
    this._addToBuffer(LogLevel.ERROR, message, { error, data });

    // Send to error tracking service
    try {
      // Dynamic import to avoid circular dependencies
      import("./errorTracking")
        .then(({ captureException, captureMessage }) => {
          if (error) {
            captureException(error, {
              extra: { message, data },
              tags: { source: "logger" },
            });
          } else {
            captureMessage(message, "error", { data });
          }
        })
        .catch(() => {
          // Error tracking not available, continue silently
        });
    } catch (e) {
      // Error tracking not available, continue silently
    }
  }

  /**
   * Get recent logs (for debugging)
   */
  getRecentLogs(level = null, limit = 50) {
    let logs = this.logs;
    if (level) {
      logs = logs.filter((log) => log.level === level);
    }
    return logs.slice(-limit);
  }

  /**
   * Clear log buffer
   */
  clear() {
    this.logs = [];
  }
}

// Export singleton instance
export const logger = new Logger();

// Export convenience functions
export const logDebug = (message, data) => logger.debug(message, data);
export const logInfo = (message, data) => logger.info(message, data);
export const logWarn = (message, data) => logger.warn(message, data);
export const logError = (message, error, data) => logger.error(message, error, data);

export default logger;
